import React from "react";
import { getStandardDeviation, lerpColor } from "@Utils";
import { RadialBar, RadialBarConfig } from "@ant-design/plots";
import { PARTIAL_VISUALISATION_CONFIGURATION } from "./visualisationConstants";

export interface IRadialChartData {
    name: string;
    group?: string;
    groupValue?: number;
    value: number;
}

interface IProps {
    colorField?: "item" | "index" | "value";
    data: IRadialChartData[];
    isStack?: boolean;
    tooltip: { customContent?: (title: string, items: unknown[]) => HTMLElement | string };
}

const RadialBarGraph: React.FC<IProps> = ({
    colorField = "value",
    data = [],
    isStack = false,
    tooltip,

    ...otherProps
}) => {
    const sort = (a: IRadialChartData, b: IRadialChartData) => a.value - b.value;
    const stackedSort = (a: IRadialChartData, b: IRadialChartData) => a.groupValue - b.groupValue;
    const graphData = data.sort(isStack ? stackedSort : sort);
    const mean = Math.floor(data.reduce((acc, cur) => acc + cur.value, 0) / data.length);
    const stDeviation = getStandardDeviation(data.map((a) => a.value));

    const stackedColours = ({ index }) => {
        const colorIndex = (index + 1) % 3;
        switch (colorIndex) {
            case 0:
                return (
                    "l(270)" +
                    " 0:" +
                    lerpColor("#457db0", "#5da3e3", "#a1c8eb", 0.1) +
                    " 1:" +
                    lerpColor("#5887b3", "#7eacd7", "#a8c3db", 0.1)
                );
            case 1:
                return (
                    "l(270)" +
                    " 0:" +
                    lerpColor("#b07d45", "#e3a35d", "#ebc8a1", 0.1) +
                    " 1:" +
                    lerpColor("#b38758", "#d7ac7e", "#dbc3a8", 0.1)
                );
            case 2:
                return (
                    "l(270)" +
                    " 0:" +
                    lerpColor("#7d45b0", "#a35de3", "#c8a1eb", 0.1) +
                    " 1:" +
                    lerpColor("#8758b3", "#ac7ed7", "#c3a8db", 0.1)
                );
            default:
                return (
                    "l(270)" +
                    " 0:" +
                    lerpColor("#45b07d", "#5de3a3", "#a1ebc8", 0.1) +
                    " 1:" +
                    lerpColor("#58b387", "#7ed7ac", "#a8dbc3", 0.1)
                );
        }
    };

    const colours = ({ value }) => {
        if (value >= mean) {
            return (
                "l(270)" +
                " 0:" +
                lerpColor("#457db0", "#5da3e3", "#a1c8eb", 0.1) +
                " 1:" +
                lerpColor("#5887b3", "#7eacd7", "#a8c3db", 0.1)
            );
        } else if (value > mean - 2 * stDeviation) {
            return (
                "l(270)" +
                " 0:" +
                lerpColor("#b07d45", "#e3a35d", "#ebc8a1", 0.1) +
                " 1:" +
                lerpColor("#b38758", "#d7ac7e", "#dbc3a8", 0.1)
            );
        }
        return (
            "l(270)" +
            " 0:" +
            lerpColor("#7d45b0", "#a35de3", "#c8a1eb", 0.1) +
            " 1:" +
            lerpColor("#8758b3", "#ac7ed7", "#c3a8db", 0.1)
        );
    };

    const swatch = isStack ? stackedColours : colours;

    const config: RadialBarConfig = {
        ...otherProps,
        ...PARTIAL_VISUALISATION_CONFIGURATION,
        color: swatch,
        colorField,
        barStyle: ({ name, _value }) => {
            const style = { cursor: "pointer" };
            if (name === otherProps["active"]) {
                return {
                    ...style,
                };
            }
            return {
                ...style,
            };
        },
        data: graphData,
        innerRadius: 0.2,
        isStack,
        maxAngle: 270,
        radius: 0.8,
        tooltip,
        xField: "name",
        yField: "value",
    };
    return <RadialBar {...config} />;
};

export default RadialBarGraph;
